// RHF migration
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/ui/sheet";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Form, FormControl, FormField, FormItem } from "@/shared/ui/form";
import { StatusBadge } from "@/shared/ui/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import {
  Loader2,
  Eye,
  Download,
  Search,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Users,
  TrendingUp,
  Award,
  BarChart3,
} from "lucide-react";
import { coachingService } from "../../api/coachingService";
import {
  fmtDatetime,
  gradeColor,
  pdfStatusType,
  SCORE_COLORS,
  scoreTone,
  taskStatusLabel,
} from "./helpers";
import { PASS_SCORE } from "@/wecom-h5/coaching/scoreGrade";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
  CartesianGrid,
} from "recharts";

const RANGE_LABEL_KEYS: Record<string, string> = {
  "90-100": "evalTaskDetail.rangeExcellent",
  "80-89": "evalTaskDetail.rangeGood",
  "70-79": "evalTaskDetail.rangeMedium",
  "60-69": "evalTaskDetail.rangePass",
  "<60": "evalTaskDetail.rangeFail",
};

type FilterStatus = "all" | "completed" | "pending";

const drawerFilterSchema = z.object({
  search: z.string(),
  filter: z.enum(["all", "completed", "pending"]),
});

type DrawerFilterValues = z.infer<typeof drawerFilterSchema>;

interface Props {
  taskId: number | null;
  onClose: () => void;
  onView: (evaluationId: number) => void;
  onDownload: (evaluationId: number) => void;
}

export function EvaluationTaskDetailDrawer({ taskId, onClose, onView, onDownload }: Props) {
  const { t } = useTranslation("coaching");
  const form = useForm<DrawerFilterValues>({
    resolver: zodResolver(drawerFilterSchema),
    mode: "onBlur",
    defaultValues: { search: "", filter: "all" },
  });
  const search = form.watch("search");
  const filter = form.watch("filter") as FilterStatus;

  const {
    data: progress,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["task-progress", taskId],
    queryFn: () => coachingService.getTaskProgress(taskId as number),
    enabled: !!taskId,
    meta: { suppressGlobalErrorToast: true },
  });

  const members = useMemo(() => progress?.members ?? [], [progress?.members]);

  const filtered = useMemo(() => {
    let rows = members;
    if (filter === "completed") rows = rows.filter((m) => m.completed);
    if (filter === "pending") rows = rows.filter((m) => !m.completed);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (m) =>
          (m.name ?? "").toLowerCase().includes(q) || (m.department ?? "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [members, filter, search]);

  // 单任务评分分布
  const scoreDist = useMemo(() => {
    const buckets: Record<string, number> = {
      "90-100": 0,
      "80-89": 0,
      "70-79": 0,
      "60-69": 0,
      "<60": 0,
    };
    for (const m of members) {
      if (!m.completed || m.score == null) continue;
      if (m.score >= 90) buckets["90-100"]++;
      else if (m.score >= 80) buckets["80-89"]++;
      else if (m.score >= 70) buckets["70-79"]++;
      else if (m.score >= 60) buckets["60-69"]++;
      else buckets["<60"]++;
    }
    return Object.entries(buckets).map(([range, count]) => ({
      range,
      count,
      color: SCORE_COLORS[range],
    }));
  }, [members]);

  // 合格率 / 配色按本任务自己的及格线(此前写死 60 / 70),旧后端不带 → 系统默认。
  const passLine = progress?.passScore ?? PASS_SCORE;
  const passRate =
    progress && progress.completedCount > 0
      ? Math.round(
          (members.filter((m) => m.completed && (m.score ?? 0) >= passLine).length /
            progress.completedCount) *
            100
        )
      : null;

  return (
    <Sheet open={!!taskId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
        {isError ? (
          // audit-0713 C1c:出错不再永久转圈
          <QueryErrorBanner isError className="mt-12" onRetry={() => void refetch()} />
        ) : isLoading || !progress ? (
          <div className="flex items-center justify-center h-40 mt-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <span>{progress.taskName}</span>
                <Badge variant="outline" className="ml-1">
                  {taskStatusLabel(progress.status)}
                </Badge>
              </SheetTitle>
              <div className="text-xs text-muted-foreground">
                {t("evalTaskDetail.scenePrefix")}
                {progress.scenarioName ?? "—"}
                {progress.deadline && (
                  <span>
                    {t("evalTaskDetail.deadlinePrefix")}
                    {fmtDatetime(progress.deadline)}
                  </span>
                )}
              </div>
            </SheetHeader>

            {/* 单任务 KPI 卡(4 张) */}
            <div className="grid grid-cols-4 gap-2 my-4">
              <Card className="shadow-sm">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-2xs text-muted-foreground">
                      {t("evalTaskDetail.kpiAssigned")}
                    </span>
                    <Users className="h-3 w-3 text-primary" />
                  </div>
                  <div className="text-xl font-bold tabular-nums">{progress.assignedCount}</div>
                </CardContent>
              </Card>
              <Card className="shadow-sm">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-2xs text-muted-foreground">
                      {t("evalTaskDetail.kpiCompleted")}
                    </span>
                    <CheckCircle2 className="h-3 w-3 text-success" />
                  </div>
                  <div className="text-xl font-bold tabular-nums text-success">
                    {progress.completedCount}
                  </div>
                </CardContent>
              </Card>
              <Card className="shadow-sm">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-2xs text-muted-foreground">
                      {t("evalTaskDetail.kpiAvg")}
                    </span>
                    <TrendingUp className="h-3 w-3 text-primary" />
                  </div>
                  <div className="text-xl font-bold tabular-nums">
                    {progress.avgScore !== null ? progress.avgScore.toFixed(1) : "—"}
                  </div>
                </CardContent>
              </Card>
              <Card className="shadow-sm">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-2xs text-muted-foreground">
                      {t("evalTaskDetail.kpiPassRate")}
                    </span>
                    <Award className="h-3 w-3 text-warning" />
                  </div>
                  <div className="text-xl font-bold tabular-nums">
                    {passRate !== null ? `${passRate}%` : "—"}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* 极值员工(若有) */}
            {(progress.topScore || progress.bottomScore) && (
              <div className="rounded-md border bg-muted/30 px-3 py-2 mb-4 text-xs space-y-1">
                {progress.topScore && (
                  <div>
                    <span className="text-success font-semibold">
                      {t("evalTaskDetail.topScore")}
                    </span>{" "}
                    <span className="font-bold tabular-nums">{progress.topScore.score}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      · {progress.topScore.employeeName}
                    </span>
                  </div>
                )}
                {progress.bottomScore && (
                  <div>
                    <span className="text-destructive font-semibold">
                      {t("evalTaskDetail.bottomScore")}
                    </span>{" "}
                    <span className="font-bold tabular-nums">{progress.bottomScore.score}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      · {progress.bottomScore.employeeName}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* 评分分布图 */}
            {progress.completedCount > 0 && (
              <Card className="shadow-sm mb-4">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs flex items-center gap-1.5 font-medium">
                      <BarChart3 className="h-3.5 w-3.5 text-primary" />
                      {t("evalTaskDetail.scoreDistribution")}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {scoreDist.map((p) => (
                        <div key={p.range} className="flex items-center gap-1">
                          <span
                            className="inline-block w-2 h-2 rounded-sm"
                            style={{ backgroundColor: p.color }}
                          />
                          <span className="text-2xs text-muted-foreground">
                            {p.range}{" "}
                            {RANGE_LABEL_KEYS[p.range] ? t(RANGE_LABEL_KEYS[p.range]) : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart
                      data={scoreDist}
                      margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                      barCategoryGap="25%"
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="hsl(var(--chart-grid))"
                      />
                      <XAxis
                        dataKey="range"
                        tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }}
                        tickLine={false}
                        axisLine={{ stroke: "hsl(var(--border))" }}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                        tickLine={false}
                        axisLine={false}
                        width={28}
                      />
                      <Tooltip
                        cursor={{ fill: "hsl(var(--muted))" }}
                        contentStyle={{
                          fontSize: 12,
                          borderRadius: 6,
                          border: "1px solid hsl(var(--border))",
                          background: "hsl(var(--popover))",
                          color: "hsl(var(--popover-foreground))",
                        }}
                        formatter={(value: number) => [
                          t("evalTaskDetail.barCount", { count: value }),
                          t("evalTaskDetail.barLabel"),
                        ]}
                        labelFormatter={(label) =>
                          `${label} ${RANGE_LABEL_KEYS[label] ? t(RANGE_LABEL_KEYS[label]) : ""}`
                        }
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={48}>
                        {scoreDist.map((p, i) => (
                          <Cell key={i} fill={p.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* 工具栏 */}
            <Form {...form}>
              <form className="flex items-center gap-2 mb-3" onSubmit={(e) => e.preventDefault()}>
                <FormField
                  control={form.control}
                  name="search"
                  render={({ field }) => (
                    <FormItem className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <FormControl>
                        <Input
                          placeholder={t("evalTaskDetail.searchPlaceholder")}
                          className="pl-9 h-9"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="filter"
                  render={({ field }) => (
                    <FormItem>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-32 h-9">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="all">{t("evalTaskDetail.filterAll")}</SelectItem>
                          <SelectItem value="completed">
                            {t("evalTaskDetail.filterCompleted")}
                          </SelectItem>
                          <SelectItem value="pending">
                            {t("evalTaskDetail.filterPending")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </form>
            </Form>

            {/* 员工紧凑表格 */}
            <Card className="shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>{t("evalTaskDetail.headerEmployee")}</TableHead>
                    <TableHead className="w-16">{t("evalTaskDetail.headerScore")}</TableHead>
                    <TableHead className="w-20">{t("evalTaskDetail.headerGrade")}</TableHead>
                    <TableHead className="w-32">{t("evalTaskDetail.headerCompletedAt")}</TableHead>
                    <TableHead className="w-24">{t("evalTaskDetail.headerPdf")}</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-xs text-muted-foreground py-8"
                      >
                        {search || filter !== "all"
                          ? t("evalTaskDetail.emptyMatch")
                          : t("evalTaskDetail.emptyMembers")}
                      </TableCell>
                    </TableRow>
                  )}
                  {filtered.map((m, idx) => {
                    const isHighRisk = m.completed && (m.score ?? 0) < 60;
                    return (
                      <TableRow
                        key={m.principalId}
                        className={`hover:bg-muted/40 fx-enter ${
                          isHighRisk ? "border-l-4 border-l-destructive bg-destructive/5" : ""
                        }`}
                        style={{ animationDelay: `${Math.min(idx, 7) * 30}ms` }}
                      >
                        <TableCell>
                          {m.completed ? (
                            <CheckCircle2 className="h-4 w-4 text-success" />
                          ) : (
                            <Clock className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{m.name ?? "—"}</div>
                          <div className="text-2xs text-muted-foreground">
                            {m.department ?? "—"}
                          </div>
                        </TableCell>
                        <TableCell>
                          {m.score != null ? (
                            <span
                              className={`text-base font-bold tabular-nums ${scoreTone(m.score, passLine)}`}
                            >
                              {m.score}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {m.grade ? (
                            <span className={`text-xs font-semibold ${gradeColor(m.grade)}`}>
                              {m.grade}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-2xs text-muted-foreground">
                          {fmtDatetime(m.completedAt ?? null)}
                        </TableCell>
                        <TableCell>
                          {m.pdfStatus ? (
                            <StatusBadge type={pdfStatusType(m.pdfStatus)}>
                              {m.pdfStatus}
                            </StatusBadge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-0.5">
                            {m.completed && m.evaluationId != null && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => onView(m.evaluationId as number)}
                                title={t("evalTaskDetail.actionView")}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {/* PDF 按需渲染(download-report 直接出 PDF,无存储/无"先生成"步骤);
                                评估完成即可下载,不再依赖永不变"已生成"的 pdfStatus 两步状态机。 */}
                            {m.completed && m.evaluationId != null && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => onDownload(m.evaluationId as number)}
                                title={t("evalTaskDetail.actionDownload")}
                              >
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>

            {/* 高风险提示 */}
            {members.filter((m) => m.completed && (m.score ?? 0) < 60).length > 0 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-destructive">
                <AlertTriangle className="h-3.5 w-3.5" />
                {t("evalTaskDetail.riskAlert", {
                  count: members.filter((m) => m.completed && (m.score ?? 0) < 60).length,
                })}
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
